import React, { useRef, useCallback, useEffect } from 'react';
import useFileViewerStore from '../../../stores/useFileViewerStore';
import { useTheme } from '../../../theme/ThemeContext';
import { isAndroid, canInlinePdf } from '../../../utils/browserDetect';
import './FileViewerModal.css';

/**
 * FileViewerModal — Global fullscreen overlay for viewing PDFs & images.
 *
 * Mount once at the app root (App.js). Opens/closes via useFileViewerStore:
 *   useFileViewerStore.getState().openViewer({ blobUrl, fileType, title })
 *   useFileViewerStore.getState().closeViewer()
 */

const FileViewerModal = () => {
  const { isOpen, blobUrl, fileType, title, closeViewer } = useFileViewerStore();
  const { isDark, theme } = useTheme();
  const iframeRef = useRef(null);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeViewer();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, closeViewer]);

  // Prevent body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handlePrint = useCallback(() => {
    if (fileType === 'pdf' && iframeRef.current) {
      try {
        iframeRef.current.contentWindow.focus();
        iframeRef.current.contentWindow.print();
      } catch {
        // Cross-origin fallback: open in new tab for printing
        window.open(blobUrl, '_blank');
      }
    } else {
      window.print();
    }
  }, [fileType, blobUrl]);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    // fileType-driven extension. 'pdf' -> .pdf, 'xlsx' -> .xlsx, everything
    // else (incl. legacy callers with no fileType or 'image'/'unknown')
    // keeps the original .png default — zero behaviour change for any
    // existing caller.
    const ext = fileType === 'pdf' ? 'pdf' : fileType === 'xlsx' ? 'xlsx' : 'png';
    a.download = title
      ? `${title.replace(/[^a-zA-Z0-9_.-]/g, '_')}${title.endsWith(`.${ext}`) ? '' : `.${ext}`}`
      : `file.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [blobUrl, title, fileType]);

  if (!isOpen || !blobUrl) return null;

  // ── Theme colors ──────────────────────────────────────────────
  const overlayBg = isDark ? '#0E0E0F' : '#f5f5f5';
  const toolbarBg = isDark ? 'rgba(22,22,24,0.97)' : 'rgba(255,255,255,0.97)';
  const toolbarBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const titleColor = isDark ? '#E8E9ED' : '#1a1a2e';
  const printBg = theme.gradient?.userMessage || 'linear-gradient(135deg, #0D9488, #0F766E)';
  const closeBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const closeColor = isDark ? '#D1D1D6' : '#374151';
  const downloadBg = isDark ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.08)';

  return (
    <div className="fvm-overlay" style={{ backgroundColor: overlayBg }}>
      {/* ── Toolbar ──────────────────────────────────────────── */}
      <div
        className="fvm-toolbar"
        style={{ background: toolbarBg, borderBottomColor: toolbarBorder }}
      >
        <span className="fvm-title" style={{ color: titleColor }}>
          {title || (fileType === 'pdf' ? 'PDF Viewer' : 'Image Viewer')}
        </span>

        <div className="fvm-actions">
          {/* Download */}
          <button
            className="fvm-btn"
            onClick={handleDownload}
            title="Download"
            style={{ background: downloadBg, color: '#2563EB' }}
          >
            ⬇ Download
          </button>

          {/* Print — only when iframe PDF rendering is available */}
          {canInlinePdf && (
            <button
              className="fvm-btn"
              onClick={handlePrint}
              title="Print"
              style={{ background: printBg, color: '#fff' }}
            >
              🖨️ Print
            </button>
          )}

          {/* Close */}
          <button
            className="fvm-btn"
            onClick={closeViewer}
            title="Close (Esc)"
            style={{ background: closeBg, color: closeColor }}
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────── */}
      <div className="fvm-body" style={{ background: overlayBg }}>
        {fileType === 'pdf' ? (
          !canInlinePdf ? (
            // Mobile browsers — iframe PDF doesn't work; offer native viewer or download
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', gap: '1.25rem', padding: '2rem',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '3rem' }}>📄</div>
              <p style={{ color: titleColor, fontSize: '0.9375rem', margin: 0, fontWeight: 500 }}>
                {title || 'Prescription'}
              </p>
              <p style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.8125rem', margin: 0 }}>
                {isAndroid
                  ? "Tap below to open the PDF in your browser's viewer"
                  : "Tap below to download the PDF and open it in your device's viewer"}
              </p>
              {/* Android: open in new tab — Chrome's top-level PDF viewer handles blob URLs */}
              {/* iOS: download — Safari cannot open blob URL PDFs in new tabs */}
              {isAndroid ? (
                <a
                  href={blobUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.75rem 1.5rem', borderRadius: '0.75rem',
                    background: printBg, color: '#fff',
                    fontSize: '0.9375rem', fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  📄 Open PDF
                </a>
              ) : (
                <a
                  href={blobUrl}
                  download={title ? `${title.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf` : 'prescription.pdf'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.75rem 1.5rem', borderRadius: '0.75rem',
                    background: printBg, color: '#fff',
                    fontSize: '0.9375rem', fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  ⬇ Download PDF
                </a>
              )}
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              className="fvm-iframe"
              src={blobUrl}
              title={title || 'PDF Viewer'}
            />
          )
        ) : fileType === 'image' ? (
          <img
            className="fvm-image"
            src={blobUrl}
            alt={title || 'File preview'}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: titleColor }}>
            <p style={{ marginBottom: 12 }}>This file type cannot be previewed inline.</p>
            <button className="fvm-btn" onClick={handleDownload} style={{ background: printBg, color: '#fff' }}>
              ⬇ Download File
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileViewerModal;
